/**
 * Rich string formatting utilities for React Native
 * Similar to Swift's StringFormattingUtils.createMarkdownAttributedString
 */

export interface RichTextToken {
  type:
    | "text"
    | "link"
    | "bullet"
    | "numbered"
    | "linebreak"
    | "bold"
    | "italic"
    | "strikethrough"
    | "underline"
    | "heading";
  content: string;
  url?: string;
  indentLevel?: number;
  listNumber?: number;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * Emits list-item tokens (bullet or numbered) with inline-formatting support.
 * The first parsed inline token is converted to the list marker type; when that
 * token is non-text (bold, italic, link, …) an empty marker token is prepended
 * so the renderer always sees a leading marker.
 */
function emitListTokens(
  tokens: RichTextToken[],
  markerType: "bullet" | "numbered",
  content: string,
  indent: number,
  listNumber?: number,
): void {
  const inlineTokens = parseInlineContent(content);
  let isFirstToken = true;
  const extra: Partial<RichTextToken> = { indentLevel: indent };
  if (markerType === "numbered") extra.listNumber = listNumber;

  inlineTokens.forEach((token) => {
    if (isFirstToken) {
      if (token.type === "text") {
        tokens.push({ ...token, type: markerType, ...extra });
      } else {
        tokens.push({ type: markerType, content: "", ...extra });
        tokens.push({ ...token, ...extra });
      }
      isFirstToken = false;
    } else {
      tokens.push({ ...token, indentLevel: indent });
    }
  });
  if (inlineTokens.length === 0) {
    tokens.push({ type: markerType, content: "", ...extra });
  }
}

/**
 * Parses rich string content into tokens for React Native rendering
 * Transforms "* " to "• " and parses markdown links and headings
 * @param content - The markdown string to parse
 * @returns Array of parsed tokens
 */
export function parseRichTextContent(content: string): RichTextToken[] {
  const tokens: RichTextToken[] = [];
  // Pre-process: Convert <br> and <br/> tags to newlines for proper line-based parsing
  // This ensures bullets and other line-based syntax work correctly with <br> tags
  const normalizedContent = content.replace(/<br\s*\/?>/gi, "\n");
  const lines = normalizedContent.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trimStart();

    // Handle headings (# H1, ## H2, etc.)
    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
      const level = headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6;
      const headingText = headingMatch[2];

      // Parse inline content within heading (bold, italic, etc.)
      const headingTokens = parseInlineContent(headingText);
      headingTokens.forEach((token) => {
        tokens.push({
          ...token,
          type: "heading",
          headingLevel: level,
        });
      });
    }
    // Handle bullet points (both * and - syntax)
    else if (trimmedLine.startsWith("* ") || trimmedLine.startsWith("- ")) {
      const indent = line.length - trimmedLine.length;
      const bulletContent = trimmedLine.slice(2);
      emitListTokens(tokens, "bullet", bulletContent, indent);
    }
    // Handle numbered lists (e.g. "1. Item", "2. Item")
    else if (/^\d+\.\s/.test(trimmedLine)) {
      const indent = line.length - trimmedLine.length;
      const dotIdx = trimmedLine.indexOf(". ");
      const num = parseInt(trimmedLine.slice(0, dotIdx), 10);
      const numberedContent = trimmedLine.slice(dotIdx + 2);
      emitListTokens(tokens, "numbered", numberedContent, indent, num);
    } else {
      // Parse regular line content
      const lineTokens = parseInlineContent(line);
      tokens.push(...lineTokens);
    }

    // Add line break except for last line
    if (i < lines.length - 1) {
      tokens.push({ type: "linebreak", content: "\n" });
    }
  }

  return tokens;
}

/**
 * Parses inline content for links, bold text, italic text, strikethrough, and underline
 * @param text - The text to parse
 * @returns Array of parsed tokens
 */
function parseInlineContent(text: string): RichTextToken[] {
  const tokens: RichTextToken[] = [];
  let i = 0;
  let currentText = "";

  while (i < text.length) {
    // Check for bold formatting **text** (must check before single *)
    if (text[i] === "*" && text[i + 1] === "*") {
      // Add any accumulated text before the bold
      if (currentText) {
        tokens.push({ type: "text", content: currentText });
        currentText = "";
      }

      // Find the closing **
      const endBold = text.indexOf("**", i + 2);
      if (endBold !== -1) {
        const boldContent = text.slice(i + 2, endBold);
        tokens.push({
          type: "bold",
          content: boldContent,
        });
        i = endBold + 2;
        continue;
      }
    }

    // Check for strikethrough formatting ~~text~~
    if (text[i] === "~" && text[i + 1] === "~") {
      // Add any accumulated text before the strikethrough
      if (currentText) {
        tokens.push({ type: "text", content: currentText });
        currentText = "";
      }

      // Find the closing ~~
      const endStrikethrough = text.indexOf("~~", i + 2);
      if (endStrikethrough !== -1) {
        const strikethroughContent = text.slice(i + 2, endStrikethrough);
        tokens.push({
          type: "strikethrough",
          content: strikethroughContent,
        });
        i = endStrikethrough + 2;
        continue;
      }
    }

    // Check for underline formatting <u>text</u>
    if (text[i] === "<" && text[i + 1] === "u" && text[i + 2] === ">") {
      // Add any accumulated text before the underline
      if (currentText) {
        tokens.push({ type: "text", content: currentText });
        currentText = "";
      }

      // Find the closing </u>
      const endUnderline = text.indexOf("</u>", i + 3);
      if (endUnderline !== -1) {
        const underlineContent = text.slice(i + 3, endUnderline);
        tokens.push({
          type: "underline",
          content: underlineContent,
        });
        i = endUnderline + 4; // Skip past </u>
        continue;
      }
    }

    // Check for line break tags <br> or <br/>
    if (text[i] === "<" && i + 2 < text.length && text[i + 1] === "b" && text[i + 2] === "r") {
      // Check for both <br> and <br/> variants
      if (i + 4 < text.length && text[i + 3] === "/" && text[i + 4] === ">") {
        // Add any accumulated text before the line break
        if (currentText) {
          tokens.push({ type: "text", content: currentText });
          currentText = "";
        }
        // Self-closing <br/> variant
        tokens.push({ type: "linebreak", content: "\n" });
        i = i + 5; // Skip past <br/>
        continue;
      } else if (i + 3 < text.length && text[i + 3] === ">") {
        // Add any accumulated text before the line break
        if (currentText) {
          tokens.push({ type: "text", content: currentText });
          currentText = "";
        }
        // Standard <br> variant
        tokens.push({ type: "linebreak", content: "\n" });
        i = i + 4; // Skip past <br>
        continue;
      }
      // If we get here, it's malformed (e.g., "<br" without ">"), treat as regular text
      // Fall through to add '<' to currentText below
    }

    // Check for italic formatting *text* or _text_
    if (text[i] === "*" || text[i] === "_") {
      const delimiter = text[i];
      // Add any accumulated text before the italic
      if (currentText) {
        tokens.push({ type: "text", content: currentText });
        currentText = "";
      }

      // Find the closing delimiter
      const endItalic = text.indexOf(delimiter, i + 1);
      if (endItalic !== -1) {
        const italicContent = text.slice(i + 1, endItalic);
        // Only treat as italic if there's content inside
        if (italicContent.length > 0) {
          tokens.push({
            type: "italic",
            content: italicContent,
          });
          i = endItalic + 1;
          continue;
        }
      }
    }

    if (text[i] === "[") {
      // Try to parse a link
      const linkResult = parseLink(text, i);
      if (linkResult) {
        // Add any accumulated text before the link
        if (currentText) {
          tokens.push({ type: "text", content: currentText });
          currentText = "";
        }

        // Add the link token
        tokens.push({
          type: "link",
          content: linkResult.text,
          url: linkResult.url,
        });

        i = linkResult.endIndex;
        continue;
      }
    }

    currentText += text[i];
    i++;
  }

  // Add any remaining text
  if (currentText) {
    tokens.push({ type: "text", content: currentText });
  }

  return tokens;
}

/**
 * Parses a markdown link [text](url) starting at the given index
 * @param text - The text to parse
 * @param startIndex - Starting index of the link
 * @returns Link info or null if not a valid link
 */
function parseLink(
  text: string,
  startIndex: number,
): { text: string; url: string; endIndex: number } | null {
  const closingBracket = text.indexOf("]", startIndex);
  if (closingBracket === -1) {
    return null;
  }

  const openParen = closingBracket + 1;
  if (text[openParen] !== "(") {
    return null;
  }

  const closingParen = text.indexOf(")", openParen);
  if (closingParen === -1) {
    return null;
  }

  const linkText = text.slice(startIndex + 1, closingBracket);
  const linkUrl = text.slice(openParen + 1, closingParen);

  return {
    text: linkText,
    url: linkUrl,
    endIndex: closingParen + 1,
  };
}
